"use client";

import { c as _c } from "react/compiler-runtime";
import { type Filter, type FilterFacet_Legacy, type FilterInfo, type FilterValue, isLocationFilter } from "../types/filter";
import type { FilterOptionTreeNode, GroupedOption, LocationOptionTreeNode, Option, OptionTree } from "../types/filtermenu";
import { urlParamToField } from "../utils/helpers";
import { useInfo } from "./use-info";
interface FilterProps {
  filterTypes: Filter[];
  group?: boolean;
  searchQuery?: string;
  sorting?: "alphabetically" | "count" | "custom";
}

/**
 * Handles both location-based and regular filters with sorting options
 * @param filterTypes - Array of filter types to process
 * @param sorting - Sort method for options ("alphabetically", "count", or "custom")
 * @param searchQuery - Query string to filter options
 * @returns Either location-based options with a tree structure or base options with a flat/grouped structure
 */
export const useOptions = t0 => {
  const $ = _c(9);
  const {
    filterTypes: t1,
    sorting: t2,
    searchQuery: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const filterTypes = t4;
  const sorting = t2 === undefined ? "count" : t2;
  const searchQuery = t3 === undefined ? "" : t3;
  const filterInfo = useInfo();
  const isLocation = filterTypes.every(isLocationFilter);
  if (isLocation) {
    let t5;
    if ($[2] !== filterInfo || $[3] !== searchQuery) {
      t5 = createLocationOptions(filterInfo, searchQuery);
      $[2] = filterInfo;
      $[3] = searchQuery;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  let t5;
  if ($[5] !== filterInfo || $[6] !== filterTypes || $[7] !== sorting) {
    t5 = createOptions(filterInfo, filterTypes, sorting);
    $[5] = filterInfo;
    $[6] = filterTypes;
    $[7] = sorting;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
interface BaseOptions {
  isLocation: false;
  options: Option[] | GroupedOption[];
}
interface LocationOptions {
  isLocation: true;
  options: OptionTree;
}

/* Section: helper functions */

/**
 * Creates filter options for non-location filters
 * @param filterInfo - Filter information containing facets and counts
 * @param filterBy - Array of filter types to process
 * @param sorting - Sort method for options
 * @returns Base options object with flat or grouped structure
 */
function createOptions(filterInfo: FilterInfo, filterBy: Filter[], sorting: "alphabetically" | "count" | "custom"): BaseOptions {
  const isGroup = filterBy.length > 1;
  const unsortedOptions: (Option | {
    label: Filter;
    options: Option[];
  })[] = [];
  for (const filterType of filterBy) {
    const field = urlParamToField[filterType] ?? filterType;
    const facetKey = `data.${field}` as FilterFacet_Legacy;
    const facetCounts = filterInfo.facetsCounts[facetKey];
    if (!facetCounts) {
      continue;
    }
    const facetOrders = filterInfo.facetsOrders?.[facetKey] || {};
    const mappedOptions = Object.entries(facetCounts).map(([filterValue, count]) => {
      const order = facetOrders[filterValue] || 0;
      return {
        value: `${filterType}:${filterValue}` as FilterValue,
        order,
        label: filterValue,
        count,
        group: isGroup ? filterType : undefined
      };
    })
    // Jobs missing the field are counted under "undefined" -> check getFacetsCountsAndOrders
    .filter(option => option.label.length > 0 && option.label !== "undefined");
    if (isGroup) {
      unsortedOptions.push({
        label: filterType,
        options: mappedOptions
      });
    } else {
      unsortedOptions.push(...mappedOptions);
    }
  }
  if (!isGroup) {
    return {
      isLocation: false,
      options: sortOptions(unsortedOptions as Option[], sorting)
    };
  }
  return {
    isLocation: false,
    options: unsortedOptions as Option[] | GroupedOption[]
  };
}
function sortOptions(options: Option[], sorting: "alphabetically" | "count" | "custom"): Option[] {
  switch (sorting) {
    case "alphabetically":
      return options.sort((a, b) => a.label.localeCompare(b.label));
    case "count":
      // Sort by count and alphabetically
      return options.sort((a, b) => {
        if (b.count - a.count !== 0) {
          return b.count - a.count;
        }
        return a.label.localeCompare(b.label);
      });
    case "custom":
      return options.sort((a, b) => {
        // Sort by custom order and alphabetically
        if (a.order - b.order) {
          return a.order - b.order;
        }
        return a.label.localeCompare(b.label);
      });
    default:
      throw new Error(`Unknown sorting: ${sorting}`);
  }
}

/**
 * Creates location-based filter options with a tree structure
 * @param filterInfo - Filter information containing the location tree
 * @param searchQuery - Query string to filter locations
 * @returns Location options object with a tree structure and keyboard navigation handlers
 */
const createLocationOptions = (filterInfo: FilterInfo, searchQuery: string): LocationOptions => {
  const {
    locationTree
  } = filterInfo;
  const cleanOptions = locationTree.map(shake);
  const sortedOptions = sortNodes(cleanOptions);
  const [filteredOptions] = applySearch(sortedOptions, searchQuery);
  const options = toList(filteredOptions);
  return {
    isLocation: true,
    options
  };
};

/* Section: helper functions */

/**
 * Cleans and transforms the location tree structure
 * Removes redundant nested nodes with same labels
 * @param node - Location tree node to clean
 * @returns Cleaned and transformed node
 */
function shake(node: FilterOptionTreeNode): LocationOptionTreeNode {
  const newNode = {
    ...node
  };
  if (newNode.children?.length === 1 && newNode.children[0].label === newNode.label) {
    const child = newNode.children[0];
    if (!child.children || child.children.length === 0 || child.children.length === 1 && child.children[0].label === newNode.label) {
      newNode.children = undefined;
    }
  }
  const transformedNode = {
    ...newNode,
    value: newNode.value.replace("data.", "") as FilterValue,
    children: newNode.children ? newNode.children.map(shake) : undefined
  } as LocationOptionTreeNode;
  return transformedNode;
}

/**
 * Sorts nodes in the location tree alphabetically
 * Handles sorting at all levels of the tree
 * @param nodes - Array of tree nodes to sort
 * @returns Sorted array of nodes
 */
function sortNodes(nodes: LocationOptionTreeNode[]) {
  // Sorts Level 1 nodes
  const sortedNodes = nodes.sort((a, b) => a.value.localeCompare(b.value));

  // Sorts Level 2 & 3 nodes
  for (const node of sortedNodes) {
    if (node.children) {
      for (const child of node.children) {
        child.children?.sort((a, b) => a.value.localeCompare(b.value));
      }
      node.children?.sort((a, b) => a.value.localeCompare(b.value));
    }
  }
  return sortedNodes;
}

/**
 * Filters the location tree based on search query
 * @param tree - Location tree to filter
 * @param query - Search query string
 * @returns Tuple of filtered tree and automatically expanded nodes
 */
function applySearch(tree: LocationOptionTreeNode[], query: string): [LocationOptionTreeNode[], FilterValue[]] {
  const queryLower = query.toLowerCase();
  const automaticallyExpanded: FilterValue[] = [];
  const markSubTree = (node: LocationOptionTreeNode, hidden: boolean): LocationOptionTreeNode => {
    if (node.children) {
      return {
        ...node,
        hidden,
        children: node.children.map(childNode => markSubTree(childNode, hidden))
      };
    }
    return {
      ...node,
      hidden
    };
  };
  function filterNodes(children: LocationOptionTreeNode[]): [LocationOptionTreeNode[], boolean] {
    let hit = false;
    const nodes = children.map(node => {
      const labelLower = node.label.toLowerCase();
      if (labelLower.includes(queryLower)) {
        // Match in this node? Return the whole unfiltered sub tree
        hit = true;
        return markSubTree(node, false);
      }
      if (node.children) {
        // Filter children and discard if no matches are found
        const [filteredChildren, hitInChildren] = filterNodes(node.children);
        // Hit in sub tree? then expand parent
        if (hitInChildren) {
          hit = true;
          automaticallyExpanded.push(node.value);
        }
        return {
          ...node,
          hidden: !hitInChildren,
          children: filteredChildren,
          value: node.value
        };
      }

      // No filter hit, hidde whole sub tree
      return markSubTree(node, true);
    });
    return [nodes, hit];
  }
  const [filteredNodes] = filterNodes(tree);
  return [filteredNodes, automaticallyExpanded];
}

/**
 * Converts a tree structure into a flat list for react-select
 * @param optionsTree - Tree structure to convert
 * @returns Flat list of options with reference to original tree
 */
function toList(optionsTree: LocationOptionTreeNode[]): OptionTree {
  const options = [] as unknown as OptionTree;
  options.optionsTree = optionsTree;
  const traverse = (node: LocationOptionTreeNode) => {
    options.push(node);
    node.children?.forEach(traverse);
  };
  optionsTree.forEach(traverse);
  return options;
}